import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import AuthGuard from './components/AuthGuard';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import useDashboardPreload from './hooks/useDashboardPreload';
import { perfNow, logPerf } from './utils/performance';

// Lazy-load routes (code splitting)
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const InventoryDashboard = lazy(() => import('./components/Dashboard/InventoryDashboard'));
const InventoryApp = lazy(() => import('./components/InventoryApp'));
const InventoryMock = lazy(() => import('./components/InventoryMock/InventoryMock'));
const FulfillmentManager = lazy(() => import('./components/FulfillmentManager'));
const OrdersManagement = lazy(() => import('./components/Dashboard/OrdersManagement'));
const Reports = lazy(() => import('./components/Reports/Reports'));
const PricingMatrix = lazy(() => import('./components/Pricing/PricingMatrix'));
const Settings = lazy(() => import('./components/Settings/Settings'));
const ShipmentCreator = lazy(() => import('./components/ShipmentCreator'));
const Automations = lazy(() => import('./components/Automations/Automations'));
const Templates = lazy(() => import('./components/Templates/Templates'));
const TeamInbox = lazy(() => import('./components/TeamInbox/TeamInbox'));
const Partners = lazy(() => import('./components/Partners/Partners'));

// Channel pages
const EbayChannel = lazy(() => import('./components/Channels/EbayChannel'));
const AmazonChannel = lazy(() => import('./components/Channels/AmazonChannel'));
const WebsiteChannel = lazy(() => import('./components/Channels/WebsiteChannel'));
const BrokerBinChannel = lazy(() => import('./components/Channels/BrokerBinChannel'));
const GenericChannel = lazy(() => import('./components/Channels/GenericChannel'));


// Mobile device detection (Android/iPhone)
const useMobileDeviceDetection = () => {
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    const checkMobileDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isAndroid = userAgent.includes('android');
      const isIOS = userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod');
      
      setIsMobileDevice(isAndroid || isIOS);
    };

    const checkOrientation = () => {
      if (window.screen && window.screen.orientation) {
        setIsLandscape(window.screen.orientation.angle === 90 || window.screen.orientation.angle === -90);
      } else {
        // Fallback for older browsers
        setIsLandscape(window.innerWidth > window.innerHeight);
      }
    };

    checkMobileDevice();
    checkOrientation();

    // Listen for orientation changes
    window.addEventListener('orientationchange', () => {
      setTimeout(checkOrientation, 100); // Small delay to get accurate values
    });
    
    window.addEventListener('resize', checkOrientation);

    return () => {
      window.removeEventListener('orientationchange', checkOrientation);
      window.removeEventListener('resize', checkOrientation);
    };
  }, []);

  return { isMobileDevice, isLandscape };
};

// Landscape orientation prompt component
const LandscapePrompt = ({ show, onDismiss }) => {
  if (!show) return null;

  return (
    <div className="landscape-prompt-overlay">
      <div className="landscape-prompt">
        <div className="landscape-prompt-icon">📱</div>
        <h3>Better Experience in Landscape</h3>
        <p>For the best experience, please rotate your device to landscape mode. The app works in portrait mode too!</p>
        <button onClick={onDismiss} className="landscape-prompt-dismiss">
          Continue in Portrait
        </button>
      </div>
    </div>
  );
};

// App content component that uses the theme
const AppContent = () => {
  const { theme } = useTheme();
  
  // Preload dashboard data for faster loading
  useDashboardPreload();

  useEffect(() => {
    const startedAt = perfNow();
    let cancelled = false;
    const idlePrefetch = () => {
      if (cancelled) return;
      // Warm likely next routes after initial mount to speed route transitions.
      Promise.allSettled([
        import('./components/Dashboard/InventoryDashboard'),
        import('./components/Dashboard/OrdersManagement'),
        import('./components/FulfillmentManager'),
      ]).finally(() => {
        logPerf('idle-route-prefetch', startedAt);
      });
    };

    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(idlePrefetch, { timeout: 3000 });
      return () => {
        cancelled = true;
        if (typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(idleId);
        }
      };
    }

    const timer = setTimeout(idlePrefetch, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);
  
  const router = React.useMemo(() => {
    return createBrowserRouter(
      [
        {
          path: '/',
          element: <Navigate to="/dashboard" replace />,
        },
        {
          path: '/dashboard',
          element: (
            <Suspense fallback={
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                background: '#F7F6F1'
              }}>
                <div style={{ 
                  color: '#242823', 
                  fontSize: '15px',
                  textAlign: 'center'
                }}>
                  Loading dashboard...
                </div>
              </div>
            }> 
              <Dashboard />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/inventory',
          element: (
            <Suspense fallback={<div />}> 
              <InventoryDashboard />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/inventory-mock',
          element: (
            <Suspense fallback={<div />}> 
              <InventoryMock />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/shipment/create',
          element: (
            <Suspense fallback={<div />}> 
              <ShipmentCreator />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/orders',
          element: (
            <Suspense fallback={<div />}> 
              <OrdersManagement />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/messages',
          element: <Navigate to="/team-inbox" replace />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/reports',
          element: (
            <Suspense fallback={<div />}> 
              <Reports />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/pricing',
          element: (
            <Suspense fallback={<div />}>
              <PricingMatrix />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/settings',
          element: (
            <Suspense fallback={<div />}> 
              <Settings />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/automations',
          element: (
            <Suspense fallback={<div />}>
              <Automations />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/templates',
          element: (
            <Suspense fallback={<div />}>
              <Templates />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/team-inbox',
          element: (
            <Suspense fallback={<div />}>
              <TeamInbox />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/partners',
          element: (
            <Suspense fallback={<div />}>
              <Partners />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/fulfillment/:orderId?',
          element: (
            <Suspense fallback={<div />}> 
              <FulfillmentManager />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />, // Keep legacy for now
        },
        {
          path: '/legacy',
          element: (
            <Suspense fallback={<div />}> 
              <InventoryApp />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />, // Legacy inventory app
        },
        // Channel pages
        {
          path: '/channels/ebay',
          element: (
            <Suspense fallback={<div />}> 
              <EbayChannel />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/channels/amazon',
          element: (
            <Suspense fallback={<div />}>
              <AmazonChannel />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/channels/website',
          element: (
            <Suspense fallback={<div />}>
              <WebsiteChannel />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/channels/walmart',
          element: (
            <Suspense fallback={<div />}> 
              <GenericChannel />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/channels/newegg',
          element: (
            <Suspense fallback={<div />}> 
              <GenericChannel />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/channels/backmarket',
          element: (
            <Suspense fallback={<div />}> 
              <GenericChannel />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/channels/hubx',
          element: (
            <Suspense fallback={<div />}> 
              <GenericChannel />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/channels/brokerbin',
          element: (
            <Suspense fallback={<div />}> 
              <BrokerBinChannel />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '/channels/:channelId',
          element: (
            <Suspense fallback={<div />}> 
              <GenericChannel />
            </Suspense>
          ),
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />,
        },
      ],
      {
        future: {
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        },
      }
    );
  }, []);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthGuard>
        <ErrorBoundary>
          <div className="App">
            <RouterProvider router={router} />
          </div>
        </ErrorBoundary>
      </AuthGuard>
    </MuiThemeProvider>
  );
};

// Main App component with theme and auth provider wrappers
const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
